import { utcMonths, utcWeeks, utcDays, utcHours, utcMonth, utcWeek, utcDay } from 'd3-time';


const reduceSeverities = severities =>
  Object.entries(severities).reduce((acc, [severity, quantity]) =>
    [{ severity, quantity }, ...acc], []);

const reduceStats = stats =>
  Object.entries(stats).reduce((acc, [limit, severities]) =>
    [...reduceSeverities(severities).map(e => ({ ...e, limit })), ...acc], []);

const flatForStatsReducer = by =>
  (acc, journey) =>
    [...reduceStats(journey.stats[by]).map(e =>
      ({ ...e, start: journey.startDateTime })), ...acc];

const filterOverspeeds = (e => e.severity !== 'noOverSpeed');
const filterPeriod = (startDate, endDate) => (e => e.startDateTime >= startDate && e.startDateTime <= endDate);


export const getOverspeedsDataSubset = (journeys, startDate, endDate) =>
  journeys
    .filter(filterPeriod(startDate.toISOString(), endDate.toISOString()))
    .reduce(flatForStatsReducer('byOccurences'), [])
    .filter(filterOverspeeds);

export const getBehavioursDataSubset = (journeys, startDate, endDate) =>
  journeys
    .filter(filterPeriod(startDate.toISOString(), endDate.toISOString()))
    .map(journey => ({ start: journey.startDateTime, behaviour: journey.stats.behaviour }));

const groupByGeneric = (data, byFunc, initialArray = {}) =>
  data.reduce((acc, elt) => {
    const by = byFunc(elt);
    acc[by] = [...(acc[by] || []), elt];
    return acc;
  }, initialArray);

export const groupByLimit = (data) => {
  const initialArray = Array.from(Array(12))
    .reduce((acc, elt, index) => ({ ...acc, [(index + 1) * 10]: [] }), {});
  return groupByGeneric(data, elt => elt.limit, initialArray);
};

export const groupByDayOfWeek = (data) => {
  const weekdayDays = utcDays(new Date('2017-01-01T00:00:00.000Z'), new Date('2017-01-07T23:59:00.000Z'))
    .map(m => m.toISOString());
  const initialArray = weekdayDays
    .reduce((acc, elt) => ({ ...acc, [elt]: [] }), {});
  return groupByGeneric(data, elt => weekdayDays[new Date(elt.start).getDay()], initialArray);
};

export const groupByHourOfDay = (data) => {
  const weekdayDays = utcHours(new Date('2017-01-01T23:00:00.000Z'), new Date('2017-01-02T22:59:00.000Z'))
    .map(d => d.toISOString());
  const initialArray = weekdayDays
    .reduce((acc, elt) => ({ ...acc, [elt]: [] }), {});
  return groupByGeneric(data, elt => weekdayDays[new Date(elt.start).getHours()], initialArray);
};
export const groupByGroupOfHourOfDay = (data) => {
  // 6-10 10-15 15-18 18-22 22-6
  const groups = [
    '6',
    '10',
    '15',
    '18',
    '22',
  ];
  const getGroupIndex = (elt) => {
    const hours = new Date(elt.start).getHours();
    switch (hours) {
      case 7:
      case 8:
      case 9:
      case 10: return 0;
      case 11:
      case 12:
      case 13:
      case 14:
      case 15: return 1;
      case 16:
      case 17:
      case 18: return 2;
      case 19:
      case 20:
      case 21:
      case 22: return 3;
      case 23:
      case 0:
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      default: return 4;
    }
  };
  const initialArray = groups
    .reduce((acc, elt) => ({ ...acc, [elt]: [] }), {});
  return groupByGeneric(data, elt => groups[getGroupIndex(elt)], initialArray);
};


const groupByDatePeriod = (data, min, max, ranger, getRange) => {
  console.log(min, max);
  const initialArray =
    ranger(getRange(min), max)
      .map(m => m.toISOString())
      .reduce((acc, elt) => ({ ...acc, [elt]: [] }), {});
  const funcBy = elt => getRange(new Date(elt.start)).toISOString();

  return groupByGeneric(data, funcBy, initialArray);
};

export const groupByMonth = (data, min, max) => groupByDatePeriod(data, min, max, utcMonths, utcMonth);
export const groupByWeek = (data, min, max) => groupByDatePeriod(data, min, max, utcWeeks, utcWeek);
export const groupByDay = (data, min, max) => groupByDatePeriod(data, min, max, utcDays, utcDay);


export const aggregateOverspeedsBySeverity = groups => Object.entries(groups).map(([mkey, statElts]) => ({
  group: mkey,
  overspeeds: statElts.reduce((groupAcc, statElt) =>
    ({
      ...groupAcc,
      [statElt.severity]: groupAcc[statElt.severity] + statElt.quantity,
    }), {
    small: 0, medium: 0, large: 0, xlarge: 0,
  }),
}));


export const aggregateOverspeeds = groups => groups.map(group =>
  ({
    ...group,
    overspeeds: Object.values(group.overspeeds).reduce((acc, value) => acc + value),
  }));

export const aggregateBehavioursByType = groups => Object.entries(groups).map(([mkey, statElts]) => ({
  group: mkey,
  behaviour: statElts.reduce((groupAcc, statElt) =>
    ({
      accelerations: groupAcc.accelerations + ((statElt.behaviour || {}).accelerations || 0),
      brakings: groupAcc.brakings + ((statElt.behaviour || {}).brakings || 0),
    }), {
    accelerations: 0, brakings: 0,
  }),
}));

const getTickerDayFormatOptions = (grouping) => {
  switch (grouping) {
    default:
    case 'DAYLY': return { month: 'long', day: 'numeric', weekday: 'short' };
    case 'WEEKLY': return { month: 'long', day: 'numeric' };
    case 'MONTHLY': return { month: 'long' };
    case 'DAY_OF_WEEK': return { weekday: 'long' };
    case 'HOUR_OF_DAY':
    case 'GROUP_OF_HOUR_OF_DAY': return { hour: 'numeric', minute: 'numeric' };
  }
};

const formatTickerPeriod = (intl, { start, end }, formatDateOptions = { hour: 'numeric', minute: 'numeric' }) =>
  `${intl.formatDate(start, formatDateOptions)} — ${intl.formatDate(end, formatDateOptions)}`;
const getTickerForPeriod = periodIndex => (intl) => {
  const dates = [
    new Date('2017-01-01T05:00:00.000Z'),
    new Date('2017-01-01T09:00:00.000Z'),
    new Date('2017-01-01T14:00:00.000Z'),
    new Date('2017-01-01T17:00:00.000Z'),
    new Date('2017-01-01T21:00:00.000Z'),
  ];
  switch (periodIndex) {
    default:
    case '6': return formatTickerPeriod(intl, { start: dates[0], end: dates[1] });
    case '10': return formatTickerPeriod(intl, { start: dates[1], end: dates[2] });
    case '15': return formatTickerPeriod(intl, { start: dates[2], end: dates[3] });
    case '18': return formatTickerPeriod(intl, { start: dates[3], end: dates[4] });
    case '22': return formatTickerPeriod(intl, { start: dates[4], end: dates[0] });
  }
};

export const tickerFormatter = intl => (grouping, startDate, endDate) => (timeStr) => {
  switch (grouping) {
    default:
    case 'DAYLY':
    case 'WEEKLY':
    case 'MONTHLY':
    case 'DAY_OF_WEEK':
    case 'HOUR_OF_DAY': return intl.formatDate(timeStr, getTickerDayFormatOptions(grouping));
    case 'GROUP_OF_HOUR_OF_DAY': return getTickerForPeriod(timeStr)(intl);
    case 'LIMIT': return `${timeStr}km/h`;
    case 'NO_GROUPING': return formatTickerPeriod(intl, { start: startDate, end: endDate }, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  }
};



// WEBPACK FOOTER //
// ./src/utils/charts-common.js